/**
 * Rewards won without internet.
 *
 * With no connection the app swaps the scan challenge for a harder sum, and a
 * user who beats it in time is paid — but nothing about it can be checked
 * from here, and airplane mode is the user's to switch on. So every one is
 * flagged, counted per account over the fraud policy's window, and the next
 * one past the allowance is held in the review queue instead of paid.
 *
 * This is where an operator sees who keeps doing it. The ban itself is the
 * account's own Deactivate action, one click away on the user.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlarmClock,
  Ban,
  ListFilter,
  ScrollText,
  ShieldAlert,
  Trophy,
  UserRound,
  Users,
  WifiOff,
  X,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNote,
  IdChip,
  Pagination,
  Skeleton,
  StatTile,
  type Column,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { formatDateTime, formatNumber, formatRelative } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { useOpenRecord } from '../../lib/navigation';
import type {
  AdminAlarmEvent,
  AdminOfflineStats,
  AdminOfflineUser,
  Paginated,
} from '../../lib/types';
import { EventSummary, RiskBadge } from './AlarmEvents';

export function OfflineRewards() {
  const t = useT();
  const open = useOpenRecord();
  const [usersPage, setUsersPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [focus, setFocus] = useState<AdminOfflineUser | null>(null);

  const stats = useQuery({
    queryKey: ['offline-stats', usersPage],
    queryFn: () =>
      api.get<AdminOfflineStats>('/api/events/offline/stats', {
        page: usersPage,
        limit: 25,
      }),
  });

  const events = useQuery({
    queryKey: ['offline-events', focus?.userId ?? null, eventsPage],
    queryFn: () =>
      api.get<Paginated<AdminAlarmEvent>>(
        '/api/events/offline',
        compact({ userId: focus?.userId, page: eventsPage, limit: 10 }),
      ),
  });

  const showEventsOf = (user: AdminOfflineUser | null) => {
    setFocus(user);
    setEventsPage(1);
  };

  if (stats.isPending) return <Skeleton className="h-96" />;
  if (stats.isError) return <ErrorNote error={stats.error} />;

  const { totals, allowance, windowDays, users } = stats.data;

  const columns: Column<AdminOfflineUser>[] = [
    {
      key: 'account',
      header: t('offline.column.account'),
      render: (user) => (
        <div className="flex flex-wrap items-center gap-2">
          <IdChip
            id={user.deviceId ?? user.userId}
            title={user.deviceId ? t('common.deviceId') : t('common.userId')}
          />
          {user.overAllowance && (
            <Badge tone="danger" icon={<ShieldAlert size={12} />}>
              {t('offline.overAllowance')}
            </Badge>
          )}
          {user.isActive === false && (
            <Badge tone="neutral" icon={<Ban size={12} />}>
              {t('offline.deactivated')}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'completions',
      header: t('offline.column.completions'),
      numeric: true,
      render: (user) => (
        <span className="font-semibold">
          {formatNumber(user.completions)}
        </span>
      ),
    },
    {
      key: 'outcomes',
      header: t('offline.column.outcomes'),
      numeric: true,
      render: (user) => (
        <span className="text-ink-2">
          {t('offline.outcomes', {
            earned: user.earned,
            withheld: user.withheld,
            lost: user.lost,
          })}
        </span>
      ),
    },
    {
      key: 'last',
      header: t('offline.column.last'),
      numeric: true,
      desktopOnly: true,
      render: (user) => (
        <span className="text-ink-2">{formatRelative(user.lastAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      numeric: true,
      render: (user) => (
        <Button
          variant="ghost"
          icon={<ListFilter size={14} />}
          onClick={(event) => {
            // The row itself opens the account; this only filters below.
            event.stopPropagation();
            showEventsOf(user);
          }}
        >
          {t('offline.showEvents')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          icon={<WifiOff size={14} />}
          label={t('offline.stat.completions')}
          value={formatNumber(totals.completions)}
          caption={t('offline.stat.window', { days: windowDays })}
          tone="brand"
        />
        <StatTile
          icon={<Users size={14} />}
          label={t('offline.stat.users')}
          value={formatNumber(totals.users)}
          caption={t('offline.stat.allowance', { allowance, days: windowDays })}
        />
        <StatTile
          icon={<ShieldAlert size={14} />}
          label={t('offline.stat.repeatUsers')}
          value={formatNumber(totals.repeatUsers)}
          caption={t('offline.stat.repeatCaption')}
          tone={totals.repeatUsers > 0 ? 'danger' : 'neutral'}
        />
        <StatTile
          icon={<Trophy size={14} />}
          label={t('offline.stat.outcomes')}
          value={formatNumber(totals.earned)}
          caption={t('offline.stat.outcomesCaption', {
            withheld: totals.withheld,
            lost: totals.lost,
          })}
          tone={totals.withheld > 0 ? 'warn' : 'good'}
        />
      </div>

      <Card>
        <CardHeader
          title={t('offline.users.title')}
          description={t('offline.users.description', {
            allowance,
            days: windowDays,
          })}
        />
        <DataTable
          columns={columns}
          rows={users.items}
          rowKey={(user) => user.userId}
          onRowClick={(user) => open({ kind: 'user', id: user.userId })}
          empty={
            <EmptyState
              icon={<WifiOff size={28} />}
              title={t('offline.users.emptyTitle')}
              description={t('offline.users.emptyDescription', {
                days: windowDays,
              })}
            />
          }
        />
        <Pagination
          page={users.page}
          totalPages={users.totalPages}
          total={users.total}
          onChange={setUsersPage}
        />
      </Card>

      <Card>
        <CardHeader
          title={t('offline.events.title')}
          description={t('offline.events.description')}
        />
        {focus && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5 sm:px-5">
            <span className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
              {t('offline.events.focused')}
              <IdChip id={focus.deviceId ?? focus.userId} />
            </span>
            <Button
              variant="ghost"
              icon={<X size={14} />}
              onClick={() => showEventsOf(null)}
            >
              {t('offline.events.showAll')}
            </Button>
          </div>
        )}

        {events.isPending ? (
          <Skeleton className="m-4 h-40" />
        ) : events.isError ? (
          <div className="p-4">
            <ErrorNote error={events.error} />
          </div>
        ) : events.data.items.length === 0 ? (
          <EmptyState
            icon={<ScrollText size={28} />}
            title={t('offline.events.emptyTitle')}
          />
        ) : (
          <>
            <ul className="divide-y divide-line">
              {events.data.items.map((event) => (
                <OfflineEventRow key={event.id} event={event} />
              ))}
            </ul>
            <Pagination
              page={events.data.page}
              totalPages={events.data.totalPages}
              total={events.data.total}
              onChange={setEventsPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}

/** What the server made of the claim, in the words an operator rules by. */
function DecisionBadge({ type }: { type: string }) {
  const t = useT();
  switch (type) {
    case 'reward_earned':
      return <Badge tone="good">{t('offline.decision.earned')}</Badge>;
    case 'reward_withheld':
      return <Badge tone="warn">{t('offline.decision.withheld')}</Badge>;
    case 'reward_missed':
      return <Badge tone="danger">{t('offline.decision.missed')}</Badge>;
    default:
      return <Badge tone="danger">{t('offline.decision.refused')}</Badge>;
  }
}

function OfflineEventRow({ event }: { event: AdminAlarmEvent }) {
  const t = useT();
  const open = useOpenRecord();
  const { alarmId } = event;

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-wrap items-center gap-2">
        <IdChip id={event.userId} title={t('common.userId')} />
        <DecisionBadge type={event.type} />
        {event.risk && <RiskBadge risk={event.risk} />}
        <span className="text-xs text-ink-3">
          {formatDateTime(event.occurredAt)}
        </span>
      </div>

      <p className="mt-1.5">
        <EventSummary event={event} />
      </p>

      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button
          icon={<UserRound size={15} />}
          onClick={() => open({ kind: 'user', id: event.userId })}
        >
          {t('events.viewUser')}
        </Button>
        {alarmId && (
          <Button
            icon={<AlarmClock size={15} />}
            onClick={() => open({ kind: 'alarm', id: alarmId })}
          >
            {t('events.viewAlarm')}
          </Button>
        )}
        <Button
          icon={<ScrollText size={15} />}
          onClick={() => open({ kind: 'event', id: event.id })}
        >
          {t('events.viewEvent')}
        </Button>
      </div>
    </li>
  );
}
